// Notification sound — a soft two-tone "ding" synthesized with the Web Audio API.
// No binary asset is shipped. Browser autoplay policy requires the AudioContext
// to be created/resumed inside a user gesture, so `unlockNotificationAudio()`
// resumes it on the first pointer/key event. The on/off preference is persisted
// in localStorage (default on), mirroring the theme toggle.

const SOUND_PREF_KEY = "ubik-notification-sound";

let _ctx: AudioContext | null = null;
let _unlockBound = false;

export function isNotificationSoundEnabled(): boolean {
  try {
    return localStorage.getItem(SOUND_PREF_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setNotificationSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(SOUND_PREF_KEY, enabled ? "on" : "off");
  } catch {
    /* storage unavailable — no-op */
  }
}

function getContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const AC: typeof AudioContext | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  if (!_ctx) {
    try {
      _ctx = new AC();
    } catch {
      return null;
    }
  }
  return _ctx;
}

/** Resume the AudioContext on the first user gesture (once). Safe to call repeatedly. */
export function unlockNotificationAudio(): void {
  if (_unlockBound || typeof window === "undefined") return;
  _unlockBound = true;
  const resume = () => {
    const c = getContext();
    if (c && c.state === "suspended") c.resume().catch(() => {});
    window.removeEventListener("pointerdown", resume);
    window.removeEventListener("keydown", resume);
  };
  window.addEventListener("pointerdown", resume, { once: true });
  window.addEventListener("keydown", resume, { once: true });
}

/** Play the notification ding, unless the user has muted it. Best-effort. */
export function playNotificationSound(): void {
  if (!isNotificationSoundEnabled()) return;
  const c = getContext();
  if (!c) return;
  if (c.state === "suspended") c.resume().catch(() => {});
  const now = c.currentTime;
  const tones = [
    { freq: 880, at: 0 },      // A5
    { freq: 1174.7, at: 0.11 }, // D6
  ];
  for (const { freq, at } of tones) {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, now + at);
    osc.connect(gain);
    gain.connect(c.destination);
    // Quick attack, gentle decay — quiet enough not to startle.
    gain.gain.setValueAtTime(0.0001, now + at);
    gain.gain.exponentialRampToValueAtTime(0.14, now + at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.22);
    osc.start(now + at);
    osc.stop(now + at + 0.24);
  }
}
